import { useState } from "react";
import { Modal } from "components/core/basic/RACModal";
import { Flex } from "../../../../../styled-system/jsx";
import { Button } from "components/core/basic/Button";
import { css } from "../../../../../styled-system/css";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "zodios/client";
import { Select } from "components/fields/base/Select";
import { TextField } from "components/fields/base/TextField";
import { FaPencil, FaPlus, FaTrash } from "react-icons/fa6";
import { useFieldArray, useForm, Controller, type SubmitHandler } from "react-hook-form";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import { useAppSelector } from "redux/hooks";
import { ToggleButton, ToggleButtonGroup, TooltipTrigger } from "react-aria-components";
import { useAddTargetsToAd } from "modules/advertising/api/useAddTargets";
import { type AddTargetsToAdUpdate } from "zodios/apiDefinitions/api/ads";
import { TextAreaField } from "components/fields/base/TextAreaField";
import { Tooltip } from "components/core/basic/Tooltip";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { flex, vstack } from "../../../../../styled-system/patterns";
import useAds from "hooks/useAds";
import AdvertisementSelector from "views/advertising/AdvertisementSelector";

type TargetSpec = {
  text: string;
  matchTypes: string[];
};

type FormValues = {
  targets: TargetSpec[];
  adGroup: string;
  adIds: number[];
};

type Props = {
  adId?: number;
  isNTMAd: boolean;
  targetType: string;
  initialData?: FormValues;
  buttonText?: string;
  renderAdSelector?: boolean;
  isDisabled?: boolean;
  ntmAdIds?: number[];
  onReset?: () => void;
  descriptionText?: string;
};

type MatchType = keyof typeof MATCH_TYPE_DISPLAY_MAP;

type MatchTypeOptionProps = {
  matchType: MatchType;
  isSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
};
const getTargetTextSchema = (targetType: string) =>
  z
    .string()
    .trim()
    .nonempty("Target cannot be empty.")
    .refine((kw) => (targetType === "target" ? VALID_ASIN_REGEX.test(kw) : kw.length <= 80), {
      message: targetType === "target" ? "Invalid ASIN provided." : "Keyword must be 80 characters or less.",
    })
    .refine((kw) => targetType === "target" || (!kw.startsWith(".") && !kw.endsWith(".")), {
      message: "Keyword can't start or end with a period.",
    })
    .refine((kw) => targetType === "target" || (!kw.startsWith("-") && !kw.endsWith("-")), {
      message: "Keyword can't start or end with a hyphen.",
    })
    .refine((kw) => targetType === "target" || !kw.endsWith("+"), {
      message: "Keyword can't end with a plus sign.",
    })
    .refine((kw) => targetType === "target" || (VALID_KW_REGEX.test(kw) && !kw.includes("$") && !kw.includes("_")), {
      message: "Keyword contains invalid characters.",
    });

const getFormSchema = (targetType: string, adId: number | undefined) =>
  z
    .object({
      adIds: z.array(z.number()).optional(),
      targets: z
        .array(
          z
            .object({
              text: getTargetTextSchema(targetType),
            })
            .passthrough(),
        )
        .nonempty("At least one target is required."),
    })
    .passthrough()
    .refine(
      (data) => {
        if (targetType === "target") {
          return (Array.isArray(data.adIds) && data.adIds.length === 1) || adId;
        }
        return true;
      },
      {
        message: "At least one ad must be selected.",
        path: ["adIds"],
      },
    );

const DEFAULT_FORM_DATA = {
  adGroup: "",
  targets: [{ text: "", matchTypes: [] }],
  adIds: [],
};

const VALID_ASIN_REGEX = /^[a-zA-Z0-9]{10}$/;
const VALID_KW_REGEX =
  /^([\]\w&+'".Á®ÉÓÜóüÄáŒÖßúäöÛéÀÂâÿŸàèÙçœæÔÆñíÇêëûÈÏéÊïÍîËôÚÎùÑąĄćĆęĘłŁńŃśŚźŹżŻåÅ¿¡«»ĳĲ\s\-\\/-[\u3000-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF\s+]*)$/;

const MATCH_TYPE_DISPLAY_MAP = {
  exact: "E",
  broad: "B",
  phrase: "P",
  negative_exact: "NE",
  negative_phrase: "NP",
  negative_product: "N",
} as const;

const MatchTypeOption = ({ matchType, isSelected, onChange }: MatchTypeOptionProps) => {
  return (
    <TooltipTrigger>
      <Tooltip>{snakeCaseToSentenceCase(matchType)}</Tooltip>
      <ToggleButton
        className={({ isSelected }) =>
          css({
            cursor: "pointer",
            border: "none",
            borderRadius: "standard",
            padding: "xsmall",
            color: isSelected ? "trellisPalette.2" : "#999",
            fontSize: "xlarge",
            fontWeight: isSelected ? "extrabold" : "bold",
            backgroundColor: "white",
          })
        }
        aria-label={snakeCaseToSentenceCase(matchType)}
        isSelected={isSelected}
        onChange={onChange}
      >
        {MATCH_TYPE_DISPLAY_MAP[matchType]}
      </ToggleButton>
    </TooltipTrigger>
  );
};

interface MatchTypesFieldProps {
  options: string[];
  selectedKeys: string[];
  onSelectionChange: (selectedKeys: string[], lastToggledKey: string, lastIsSelected: boolean) => void;
}

function MatchTypesField({ options, selectedKeys, onSelectionChange }: MatchTypesFieldProps) {
  return (
    <ToggleButtonGroup
      selectedKeys={selectedKeys}
      className={flex({
        gap: "medium",
        justifyContent: "end",
      })}
    >
      {options.map((option) => (
        <MatchTypeOption
          key={option}
          matchType={option as MatchType}
          isSelected={selectedKeys.includes(option)}
          onChange={(isSelected) => {
            // Don't allow opposing match types at the same time (i.e. negative_exact and exact)
            const isNegative = option.startsWith("negative");
            const oppositeMatchType = isNegative ? option.replace("negative_", "") : `negative_${option}`;

            const nextSelection =
              selectedKeys.includes(option) ?
                selectedKeys.filter((selectedMatchType) => selectedMatchType !== option)
              : [...selectedKeys.filter((prevMatchType) => prevMatchType !== oppositeMatchType), option];

            onSelectionChange(nextSelection, option, isSelected);
          }}
        />
      ))}
    </ToggleButtonGroup>
  );
}

function getAvailableMatchTypeOptions(targetType: string, currentChannel: string) {
  if (targetType === "keyword") {
    if (currentChannel === "amazon") {
      return ["exact", "phrase", "broad", "negative_exact", "negative_phrase"];
    }
    return ["exact", "phrase", "broad"];
  } else if (targetType === "target" && currentChannel === "amazon") {
    return ["negative_product"];
  }
  return [];
}

const AddTargetsWidget = ({
  adId,
  targetType,
  isNTMAd,
  buttonText,
  initialData,
  renderAdSelector = false,
  isDisabled = false,
  ntmAdIds = [],
  onReset,
  descriptionText,
}: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [isBulkInputMode, setIsBulkInputMode] = useState(false);
  const [bulkInputTargets, setBulkInputTargets] = useState("");

  const channel = useAppSelector((state) => state.channel);
  // Below is to start fetching ads used in ad selector field ASAP since it can take a few seconds
  useAds();

  const addTargetsMutation = useAddTargetsToAd();

  const schema = getFormSchema(targetType, adId);
  const initialFormData = { ...DEFAULT_FORM_DATA, ...initialData };

  const { control, setValue, reset, handleSubmit, formState, watch } = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: initialFormData,
    resolver: zodResolver(schema),
  });

  const {
    fields: targets,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "targets",
  });

  const watchedTargets = watch("targets");
  const selectedAdIds = watch("adIds");
  const selectedAdGroup = watch("adGroup");

  const isSubmitDisabled =
    !formState.isValid ||
    ((isNTMAd || (typeof selectedAdIds[0] === "number" && ntmAdIds.includes(selectedAdIds[0]))) && !selectedAdGroup) ||
    (!adId && selectedAdIds.length === 0) ||
    (targetType === "keyword" && watchedTargets.some((target) => !target.matchTypes.length));

  const adGroupOptionsQuery = useQuery({
    queryKey: ["adGroups", adId, selectedAdIds],
    queryFn: async () => {
      const ad = adId ?? selectedAdIds[0];
      if (typeof ad !== "number") {
        throw new Error("Valid advertisement ID is required");
      }
      return await apiClient.getAdGroups({ queries: { advertisement: ad } });
    },
    enabled: (!!adId && !!isNTMAd) || selectedAdIds.length > 0,
    select: (adGroupsRes) => {
      return adGroupsRes.map((adGroup) => ({
        label: adGroup.ad_group_name,
        value: adGroup.ad_group_id,
      }));
    },
  });

  const onSubmit: SubmitHandler<FormValues> = ({ adGroup, targets }) => {
    const destinationAdId = adId ?? selectedAdIds[0];
    if (typeof destinationAdId !== "number") {
      return;
    }

    const body: AddTargetsToAdUpdate = {
      data: {
        advertisement: adId ?? selectedAdIds[0]!,
        target_type: targetType,
        channel: channel.currentChannel,
        action: "create_targets",
      },
    };

    if (isNTMAd || ntmAdIds.includes(body.data.advertisement)) {
      body.data.ad_group = adGroup.trim();
    }

    if (targetType === "keyword") {
      body.data.targets = targets;
    }

    if (targetType === "target") {
      body.data.target_type = "product";

      const productTargets = targets;

      body.data.targets = {
        products: productTargets.map((t) => t.text),
        targetTypes: productTargets.map((t) =>
          t.matchTypes.includes("negative_product") ? "negative_product" : "product",
        ),
      };
    }
    addTargetsMutation.mutate(body);
    if (!renderAdSelector) {
      setShowModal(false);
    }
    reset(initialFormData);
    onReset?.();
  };

  const toggleAllMatchType = (matchType: MatchType, on: boolean) => {
    // Don't allow opposing match types at the same time (i.e. negative_exact and exact)
    const isNegative = matchType.startsWith("negative");
    const oppositeMatchType = isNegative ? matchType.replace("negative_", "") : `negative_${matchType}`;

    const updatedTargets = watchedTargets.map((target) => {
      const currentTypes = target.matchTypes;
      const hasMatchType = currentTypes.includes(matchType);

      let newMatchTypes = [...currentTypes].filter((mt) => mt !== oppositeMatchType);

      if (on && !hasMatchType) {
        newMatchTypes = [...newMatchTypes, matchType];
      } else if (!on && hasMatchType) {
        newMatchTypes = newMatchTypes.filter((mt) => mt !== matchType);
      }

      return { ...target, matchTypes: newMatchTypes };
    });

    updatedTargets.forEach((t, idx) => {
      setValue(`targets.${idx}.matchTypes`, t.matchTypes, { shouldValidate: true, shouldDirty: true });
    });
  };

  const matchTypeOptions = getAvailableMatchTypeOptions(targetType, channel.currentChannel);

  const matchTypesSelectedForAll = matchTypeOptions.filter((matchType) =>
    watchedTargets.every((target) => target.matchTypes.includes(matchType)),
  );

  return (
    <>
      <Modal
        isOpen={showModal}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            reset(initialFormData);
          }
          setShowModal(isOpen);
        }}
        title={`Add New ${targetType === "target" ? "ASIN" : toTitleCase(targetType)}s`}
        className={css({
          width: renderAdSelector ? "1200px" : "800px",
          height: renderAdSelector ? "1100px" : "inherit",
          overflowY: "auto",
        })}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={vstack({ gap: "medium", alignItems: "stretch", height: "100%", paddingX: "xlarge" })}
        >
          {isBulkInputMode ?
            <>
              <TextAreaField
                value={bulkInputTargets}
                onChange={(value) => {
                  setBulkInputTargets(value);
                }}
                rows={8}
                className={css({ width: "100%", fontSize: "medium" })}
                aria-label={`Enter ${targetType}s`}
              />
              <p className={css({ margin: 0, fontSize: "medium" })}>Separate {targetType}s using commas or new lines</p>
              <Flex gap={"large"} justifyContent={"center"}>
                <Button variant="secondary" onPress={() => setIsBulkInputMode(false)}>
                  Cancel
                </Button>
                <Button
                  onPress={() => {
                    const textSchema = getTargetTextSchema(targetType);
                    const targetsToAdd = [
                      ...new Set(
                        bulkInputTargets
                          .split(/[\n,]/)
                          .map((t) => t.trim())
                          .filter((t) => !!t),
                      ),
                    ];

                    const currentTargets = watchedTargets.map((t) => t.text);
                    const updatedTargets: TargetSpec[] = [];

                    targetsToAdd.forEach((targetText) => {
                      const result = textSchema.safeParse(targetText);
                      if (!result.success) {
                        return;
                      }
                      // Keep match types for existing targets, otherwise push new targets with default match type selection
                      if (currentTargets.includes(targetText)) {
                        const existingTarget = watchedTargets.find((t) => t.text === targetText);
                        if (existingTarget) {
                          updatedTargets.push(existingTarget);
                        }
                      } else {
                        updatedTargets.push({ text: targetText, matchTypes: [] });
                      }
                    });
                    if (updatedTargets.length) {
                      setValue("targets", updatedTargets, { shouldValidate: true, shouldDirty: true });
                    } else {
                      reset(DEFAULT_FORM_DATA);
                    }
                    setIsBulkInputMode(false);
                  }}
                >
                  Update {toTitleCase(targetType)}s
                </Button>
              </Flex>
            </>
          : <>
              <p className={css({ margin: 0, fontSize: "medium", textAlign: "center" })}>
                {descriptionText ?
                  descriptionText
                : targetType === "target" ?
                  `Add ASIN targets to ${adId ? "this" : "a"} media plan by entering them below.`
                : `Add keywords to ${adId ? "this" : "a"} media plan by entering them below and selecting your preferred match types. At least one match type is required for each keyword.`
                }
              </p>
              <div
                className={css({
                  display: "grid",
                  position: "relative",
                  gap: "large",
                  gridTemplateColumns: renderAdSelector ? "1fr 1fr" : "1fr",
                  _before:
                    renderAdSelector ?
                      {
                        content: '""',
                        position: "absolute",
                        top: "145px",
                        bottom: 0,
                        left: "50%",
                        width: "1px",
                        backgroundColor: "gray.200",
                        transform: "translateX(-50%)",
                      }
                    : undefined,
                })}
              >
                {renderAdSelector && (
                  <div className={css({ overflow: "auto" })}>
                    <label className={css({ color: "text.label", fontSize: "medium", fontWeight: "bold" })}>
                      Select a Media Plan to Add {toTitleCase(targetType)}s To
                    </label>
                    <Controller
                      name="adIds"
                      control={control}
                      render={({ field }) => (
                        <AdvertisementSelector
                          selectedRowIds={selectedAdIds}
                          onSelectionChange={(updatedAdIds) => {
                            field.onChange(updatedAdIds.map((id) => parseInt(id.toString())));
                            setValue("adGroup", "", { shouldDirty: true, shouldValidate: true });
                          }}
                          defaultSelectedColumns={[]}
                          enableMultiRowSelection={false}
                          canSetPageSize={false}
                          context={"addTargetsWidget"}
                        />
                      )}
                    />
                  </div>
                )}
                <div className={css({ marginTop: renderAdSelector ? "111px" : 0 })}>
                  {(isNTMAd || (typeof selectedAdIds[0] === "number" && ntmAdIds.includes(selectedAdIds[0]))) && (
                    <Controller
                      name="adGroup"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Ad Group"
                          onSelectionChange={(value) => {
                            field.onChange(value);
                          }}
                          selectedKey={field.value}
                          items={adGroupOptionsQuery.data}
                          className={css({ marginLeft: "xlarge" })}
                        />
                      )}
                    />
                  )}
                  <Flex
                    gap="large"
                    alignItems={"center"}
                    paddingRight="medium"
                    scrollbarGutter={"stable"}
                    overflowY="auto"
                  >
                    <Flex
                      className={css({
                        gap: "small",
                        justifyContent: "center",
                        alignItems: "center",
                        marginLeft: renderAdSelector ? "xlarge" : "inherit",
                      })}
                    >
                      <label className={css({ color: "text.label", fontSize: "medium", fontWeight: "bold" })}>
                        {targetType === "keyword" ? "Keywords" : "Targets"}
                      </label>
                      <TooltipTrigger>
                        <Tooltip placement={"top"}>
                          <span>Bulk input a list of {targetType}s from your clipboard</span>
                        </Tooltip>
                        <Button
                          variant={"contentOnly"}
                          onPress={() => {
                            setBulkInputTargets(watchedTargets.map((t) => t.text).join("\n"));
                            setIsBulkInputMode(true);
                          }}
                        >
                          <FaPencil />
                        </Button>
                      </TooltipTrigger>
                    </Flex>
                    <div className={css({ flex: 1 })}></div>
                    {matchTypeOptions.length ?
                      <MatchTypesField
                        options={matchTypeOptions}
                        selectedKeys={matchTypesSelectedForAll}
                        onSelectionChange={(_, lastToggledKey, lastIsSelected) => {
                          toggleAllMatchType(lastToggledKey as MatchType, lastIsSelected);
                        }}
                      />
                    : null}
                    <div className={css({ minWidth: "18px" })} />
                  </Flex>
                  <Flex
                    className={css({
                      gap: "small",
                      flexDirection: "column",
                      borderTop: "1px solid token(colors.gray.200)",
                      borderBottom: "1px solid token(colors.gray.200)",
                      maxHeight: "350px",
                      paddingRight: "medium",
                      paddingTop: "small",
                      paddingBottom: "small",
                      scrollbarGutter: "stable",
                      overflowY: "auto",
                    })}
                  >
                    {targets.map((target, index) => {
                      return (
                        <Flex
                          key={target.id}
                          className={css({
                            position: "relative",
                            gap: "large",
                            alignItems: "center",
                          })}
                        >
                          {index === targets.length - 1 ?
                            <Button
                              variant={"contentOnly"}
                              className={css({
                                color: "gray",
                                fontSize: "xlarge",
                                fontWeight: "bold",
                              })}
                              onPress={() => {
                                append({ text: "", matchTypes: [] });
                              }}
                            >
                              <FaPlus />
                            </Button>
                          : <div className={css({ minWidth: "18px" })} />}
                          <Controller
                            control={control}
                            name={`targets.${index}.text`}
                            render={({ field, fieldState }) => {
                              return (
                                <TextField
                                  {...field}
                                  errorMessage={fieldState.error?.message}
                                  placeholder={
                                    targetType === "target" ? "Enter target ASINs..." : "Enter target text..."
                                  }
                                  aria-label={targetType === "target" ? "Target ASINs" : "Target text"}
                                />
                              );
                            }}
                          />
                          {matchTypeOptions.length ?
                            <Controller
                              control={control}
                              name={`targets.${index}.matchTypes`}
                              render={({ field }) => (
                                <MatchTypesField
                                  options={matchTypeOptions}
                                  selectedKeys={field.value}
                                  onSelectionChange={(value) => {
                                    field.onChange(value);
                                    field.onBlur();
                                  }}
                                />
                              )}
                            />
                          : null}

                          {targets.length > 1 ?
                            <Button
                              variant={"contentOnly"}
                              className={css({
                                color: "gray",
                                fontSize: "xlarge",
                              })}
                              onPress={() => remove(index)}
                            >
                              <FaTrash />
                            </Button>
                          : <div className={css({ minWidth: "18px" })} />}
                        </Flex>
                      );
                    })}
                  </Flex>
                  {renderAdSelector && (
                    <Flex justifyContent={"center"} padding={"large"}>
                      <Button type={"submit"} isDisabled={isSubmitDisabled}>
                        Add {toTitleCase(targetType)}
                        {targets.length === 1 ? "" : "s"}
                      </Button>
                    </Flex>
                  )}
                </div>
              </div>
              <Flex className={css({ gap: "large", justifyContent: "center", marginTop: "auto" })}>
                <Button
                  variant={"secondary"}
                  onPress={() => {
                    setShowModal(false);
                  }}
                >
                  Cancel
                </Button>
                {renderAdSelector ?
                  <Button
                    onPress={() => {
                      setShowModal(false);
                    }}
                  >
                    Finished
                  </Button>
                : <Button type={"submit"} isDisabled={isSubmitDisabled}>
                    Add {toTitleCase(targetType)}s
                  </Button>
                }
              </Flex>
            </>
          }
        </form>
      </Modal>

      <Button
        isDisabled={isDisabled}
        onPress={() => {
          setShowModal(!showModal);
        }}
      >
        {buttonText ?? `Add New ${toTitleCase(targetType)}s`}
      </Button>
    </>
  );
};

export default AddTargetsWidget;
